/**
 * `useT` (W1-12 ui-kit-i18n; contract src/i18n/types.ts): the translation function of the app runtime, as a hook
 * that re-renders its component when the locale changes. The returned function's identity changes with the locale,
 * so memoised children and effects that depend on it update too. The only i18n file that imports React.
 */

import { useMemo, useSyncExternalStore } from 'react';
import type { Locale } from '../core/util/types';
import { i18n } from './runtime';
import type { I18n, UseT } from './types';

const subscribe = (cb: () => void): (() => void) => i18n.subscribe(cb);
const getLocale = (): Locale => i18n.locale;

/** A translation function tagged with the locale it was created for (a new function per locale). */
function translatorFor(_locale: Locale): I18n['t'] {
  return (key, params) => i18n.t(key, params);
}

/** Current UI locale; re-renders on change. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}

export const useT: UseT = () => {
  const locale = useLocale();
  return useMemo(() => translatorFor(locale), [locale]);
};
